import MainCard from 'components/MainCard';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { formatToHours } from 'components/util/HoursDisplay';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Pie, PieChart } from 'recharts';
import { DashboardForm } from 'types/dashboard';
import type { HomeWidgetSize } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import FinalizedHoursLegend from './FinalizedHoursLegend';
import PeriodPicker from './PeriodPicker';
import useFinalizedHours from './useFinalizedHours';

const MAX_TEXT_WIDTH = 90;
const MAX_FONT_SIZE = 32;
const MIN_FONT_SIZE = 12;
const DONUT_SIZE = { width: 140, height: 140 };
// Drawn as the whole ring when every category is zero, so the card never shows an empty hole.
const EMPTY_RING = [{ id: 'empty', value: 1, fill: 'var(--color-grey-100)' }];

// Steps the total's font down until it fits inside the ring. A ref callback rather than an effect: Recharts
// mounts the text a render after the chart, when an effect keyed on the text has already run with no node.
const fitInside = (maxTextWidth: number, maxFontSize: number) => (el: SVGTextElement | null) => {
    if (!el) return;
    let size = maxFontSize;
    el.setAttribute('font-size', `${size}`);
    while (size > MIN_FONT_SIZE && el.getComputedTextLength() > maxTextWidth) {
        size -= 1;
        el.setAttribute('font-size', `${size}`);
    }
};
const fitInsideRing = fitInside(MAX_TEXT_WIDTH, MAX_FONT_SIZE);

/**
 * Home's rings, the total in the hole over a small caption: 104px across with a 72px hole, and 140px with a 96px one
 * over the legend of a card four rows tall.
 */
const HOME_DONUT = {
    small: { size: 104, innerRadius: 36, maxTextWidth: 64, maxFontSize: 18, totalY: -6, captionY: 12 },
    tall: { size: 140, innerRadius: 48, maxTextWidth: 84, maxFontSize: 22, totalY: -8, captionY: 14 },
};
const fitInsideHomeRing = fitInside(HOME_DONUT.small.maxTextWidth, HOME_DONUT.small.maxFontSize);
const fitInsideTallHomeRing = fitInside(HOME_DONUT.tall.maxTextWidth, HOME_DONUT.tall.maxFontSize);

type Props = {
    /** Inside the tabbed hours card, which supplies the frame and names the content on its tab. */
    embedded?: boolean;
    /** Home's My time: a ring with its legend, inside the section's card, whose header holds the period picker. */
    home?: boolean;
    /** Home's card size: the legend is capped two columns wide, and four rows tall the larger ring sits over it. */
    size?: HomeWidgetSize;
};

function FinalizedHours({ embedded = false, home = false, size }: Props) {
    const intl = useIntl();
    const { control } = useFormContext<DashboardForm>();
    const { displayDecimalScale } = useTimecardPrecision();
    const [dateFrom, dateTo] = useWatch({
        control,
        name: ['dateFrom', 'dateTo'],
    });
    const { finalizedHours, finalizedHoursData, isLoading, isLoadingWithNoPreviousData, isAllZero } = useFinalizedHours(
        {
            dateFrom,
            dateTo,
            home,
        }
    );

    const hoursText = formatToHours(finalizedHoursData?.totalHours || 0, displayDecimalScale);

    const pieData = isAllZero ? EMPTY_RING : finalizedHours.map((item) => ({ ...item, fill: item.color }));
    const chartLabel = intl.formatMessage(
        { id: 'key-value-semicolon-separated' },
        { key: intl.formatMessage({ id: 'dashboard.finalized-hours' }), value: hoursText }
    );

    // A slice's category and hours while the pointer is on it, in a box as wide as the classic ring.
    const tooltip = (position: { x: number; y: number }, className: string, left: string | number = 0) =>
        isLoading || isAllZero ? null : (
            <ChartTooltip
                position={position}
                wrapperStyle={{ width: DONUT_SIZE.width, left }}
                content={
                    <ChartTooltipContent
                        className={className}
                        hideLabel
                        formatter={(value, name, item) => (
                            <>
                                <span
                                    className="size-2.5 shrink-0 rounded-[2px]"
                                    style={{ backgroundColor: item.payload?.fill }}
                                />
                                <span className="text-muted-foreground">{name}</span>
                                <span className="text-foreground ml-auto font-medium tabular-nums">
                                    {formatToHours(Number(value), displayDecimalScale)}
                                </span>
                            </>
                        )}
                    />
                }
            />
        );

    if (home) {
        const tall = size === '1x4';
        const ring = tall ? HOME_DONUT.tall : HOME_DONUT.small;
        const radius = ring.size / 2;
        return (
            <div className={cn('@container flex items-center', tall ? 'flex-col gap-5' : 'gap-[18px]')}>
                <ChartContainer
                    config={{}}
                    initialDimension={{ width: ring.size, height: ring.size }}
                    role="img"
                    aria-label={chartLabel}
                    className={cn('aspect-square shrink-0', tall ? 'size-35' : 'size-[104px]')}
                >
                    <PieChart accessibilityLayer={false}>
                        {/*
                            Over the legend, past the ring's 18px gap or under the tall ring, so it stays inside the
                            card: the wrappers that scroll the ring would scroll for a tooltip past their edge. Where
                            the row is too narrow for it beside the ring, it is pulled back over the ring (`cqw` reads
                            the row, its nearest container).
                        */}
                        {tall
                            ? tooltip({ x: 0, y: ring.size + 8 }, 'mx-auto w-fit')
                            : tooltip(
                                  { x: ring.size + 18, y: 0 },
                                  'w-fit',
                                  `min(0px, 100cqw - ${ring.size + 18 + DONUT_SIZE.width}px)`
                              )}
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={ring.innerRadius}
                            outerRadius={radius}
                            startAngle={90}
                            endAngle={-270}
                            stroke="var(--color-paper)"
                            isAnimationActive={false}
                            rootTabIndex={-1}
                        />
                        <g
                            transform={`translate(${radius}, ${radius})`}
                            className={cn('font-sans transition-opacity', isLoading && 'opacity-35')}
                        >
                            <text
                                key={hoursText}
                                ref={tall ? fitInsideTallHomeRing : fitInsideHomeRing}
                                y={ring.totalY}
                                dy="0.35em"
                                textAnchor="middle"
                                fontSize={ring.maxFontSize}
                                fontWeight={600}
                                className="fill-foreground tabular-nums"
                            >
                                {hoursText}
                            </text>
                            <text
                                y={ring.captionY}
                                dy="0.35em"
                                textAnchor="middle"
                                className="fill-muted-foreground text-caption"
                            >
                                {intl.formatMessage({ id: 'home.finalised-caption' })}
                            </text>
                        </g>
                    </PieChart>
                </ChartContainer>
                <FinalizedHoursLegend data={finalizedHours} home size={size} />
            </div>
        );
    }

    const content = (
        <div className={cn('flex flex-col gap-2.5', !embedded && 'px-4 pt-4 pb-5')}>
            <div className="flex flex-row justify-between gap-2">
                {embedded ? null : (
                    <h5 className="text-h5">
                        <FormattedMessage id="dashboard.finalized-hours" />
                    </h5>
                )}
                <div className="ml-auto">
                    <PeriodPicker />
                </div>
            </div>
            <div className="flex flex-col items-center">
                <ChartContainer
                    config={{}}
                    initialDimension={DONUT_SIZE}
                    role="img"
                    aria-label={chartLabel}
                    className="aspect-square size-35"
                >
                    <PieChart accessibilityLayer={false}>
                        {/*
                            Centred under the ring. Inside the 140px box it would cover the total; beside the ring a
                            narrow card has no room, and its overflow would clip it or scroll the tab.
                        */}
                        {tooltip({ x: 0, y: DONUT_SIZE.height + 8 }, 'mx-auto w-fit')}
                        {/* Starts at 12 o'clock and runs clockwise. */}
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={56}
                            outerRadius={70}
                            cornerRadius={2}
                            startAngle={90}
                            endAngle={-270}
                            stroke="var(--color-paper)"
                            isAnimationActive={isLoadingWithNoPreviousData ? false : 'auto'}
                            rootTabIndex={-1}
                        />
                        <g
                            transform="translate(70, 70)"
                            className={cn('font-sans transition-opacity', isLoading && 'opacity-35')}
                        >
                            <text
                                // A new text remounts, so the ref callback fits it again.
                                key={hoursText}
                                ref={fitInsideRing}
                                x={0}
                                y={0}
                                dy="0.35em"
                                textAnchor="middle"
                                fontSize={MAX_FONT_SIZE}
                                fontWeight={700}
                                className="fill-fg"
                            >
                                {hoursText}
                            </text>
                            <text
                                x={0}
                                y={20}
                                dy="0.35em"
                                textAnchor="middle"
                                fontSize={16}
                                fontWeight={600}
                                className="fill-fg"
                            >
                                {intl.formatMessage({ id: 'finalized-hours.total' })}
                            </text>
                        </g>
                    </PieChart>
                </ChartContainer>
                <FinalizedHoursLegend data={finalizedHours} />
            </div>
        </div>
    );

    if (embedded) {
        return content;
    }
    return <MainCard content={false}>{content}</MainCard>;
}

export default FinalizedHours;
